/**
 * The calendar's API modules: the date-bounded activity rows, the calendar periods,
 * and the stage-linked taxonomies (M4 Phase 3, step 0's additive contract).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Every function states the shape it was promised, field by field, and decides
 * nothing about what this principal may see: the operator sees the assessors the
 * server names, the contractor sees the dates the server answers with, and absence
 * answers as absence all the way down.
 */

import { apiJson, listBody, num, optStr, str, strArray, query } from "./client";
import { negotiationState, type NegotiationState } from "./schedules";

/* ---------------------------------------------------------------------- */
/* The calendar activity row (CalendarActivitySummary).                    */
/* ---------------------------------------------------------------------- */

export interface CalendarActivity {
  readonly id: string;
  readonly humanId: string;
  readonly scheduleId: string;
  readonly plannedDate: string;
  readonly plannedEndDate: string | null;
  readonly status: string;
  readonly statusLabel: string;
  readonly priority: string | null;
  readonly purpose: string | null;
  readonly contractorPartyId: string;
  readonly contractorPartyName: string;
  readonly verificationTypeCode: string;
  readonly verificationTypeLabel: string;
  readonly disciplineCode: string | null;
  readonly disciplineLabel: string | null;
  readonly assessorDisplayNames: readonly string[];
  /** Wave W2: whose turn it is on the date; the legend and the dot read it. */
  readonly negotiationState: NegotiationState;
  readonly pendingDate: string | null;
  /** Wave Experience A: an open act of mine is about this activity or its schedule. */
  readonly actionableByMe?: boolean;
}

function activity(body: Record<string, unknown>): CalendarActivity {
  return {
    id: str(body.id),
    humanId: str(body.human_id),
    scheduleId: str(body.schedule_id),
    plannedDate: str(body.planned_date),
    plannedEndDate: optStr(body.planned_end_date),
    status: str(body.status),
    statusLabel: str(body.status_label),
    priority: optStr(body.priority),
    purpose: optStr(body.purpose),
    contractorPartyId: str(body.contractor_party_id),
    contractorPartyName: str(body.contractor_party_name),
    verificationTypeCode: str(body.verification_type_code),
    verificationTypeLabel: str(body.verification_type_label),
    disciplineCode: optStr(body.discipline_code),
    disciplineLabel: optStr(body.discipline_label),
    assessorDisplayNames: strArray(body.assessor_display_names),
    negotiationState: negotiationState(body.negotiation_state),
    actionableByMe: body.actionable_by_me === true,
    pendingDate: optStr(body.pending_date),
  };
}

export async function listCalendarActivities(
  token: string,
  filters: {
    fromDate?: string | null;
    toDate?: string | null;
    mine?: boolean;
    contractorPartyId?: string | null;
    /** Wave W2: the calendar reads a whole year at once (the server allows up to 500). */
    limit?: number;
    signal?: AbortSignal;
  } = {},
): Promise<{ total: number; items: CalendarActivity[] }> {
  const body = await apiJson(
    token,
    `/activities${query({
      from_date: filters.fromDate,
      to_date: filters.toDate,
      mine: filters.mine,
      contractor_party_id: filters.contractorPartyId,
      limit: filters.limit,
    })}`,
    listBody,
    { signal: filters.signal },
  );
  return { total: body.total, items: body.items.map(activity) };
}

/* ---------------------------------------------------------------------- */
/* The calendar periods (CalendarPeriodOut).                               */
/* ---------------------------------------------------------------------- */

export interface CalendarPeriod {
  readonly id: string;
  readonly kind: string;
  readonly kindLabel: string;
  /** The constraint_enforcement token: WARN or BLOCK. */
  readonly enforcement: string;
  readonly name: string;
  readonly startsOn: string;
  readonly endsOn: string;
}

export async function listCalendarPeriods(
  token: string,
  signal?: AbortSignal,
): Promise<CalendarPeriod[]> {
  return apiJson(
    token,
    "/calendar-periods",
    (body) => {
      if (!Array.isArray(body)) {
        throw new Error("Expected the calendar period list");
      }
      return body.map((entry) => {
        if (typeof entry !== "object" || entry === null) {
          throw new Error("Expected a calendar period");
        }
        const record = entry as Record<string, unknown>;
        return {
          id: str(record.id),
          kind: str(record.kind),
          kindLabel: str(record.kind_label),
          enforcement: str(record.enforcement),
          name: str(record.name),
          startsOn: str(record.starts_on),
          endsOn: str(record.ends_on),
        } satisfies CalendarPeriod;
      });
    },
    { signal },
  );
}

/** Wave Experience A: where the caller may declare a blocked period (the server's word). */
export interface PeriodCreateOptions {
  readonly tenantWide: boolean;
  readonly projects: readonly { id: string; humanId: string; name: string }[];
}

export async function getPeriodCreateOptions(
  token: string,
  signal?: AbortSignal,
): Promise<PeriodCreateOptions> {
  return apiJson(
    token,
    "/calendar-periods/create-options",
    (body) => {
      if (typeof body !== "object" || body === null) {
        throw new Error("Expected the period options");
      }
      const record = body as Record<string, unknown>;
      const projects = Array.isArray(record.projects) ? record.projects : [];
      return {
        tenantWide: record.tenant_wide === true,
        projects: projects.map((entry) => {
          const one = (typeof entry === "object" && entry !== null ? entry : {}) as Record<
            string,
            unknown
          >;
          return { id: str(one.id), humanId: str(one.human_id), name: str(one.name) };
        }),
      };
    },
    { signal },
  );
}

export interface PeriodDraft {
  readonly kind: string;
  readonly name: string;
  readonly startsOn: string;
  readonly endsOn: string;
  readonly projectId: string | null;
  readonly note: string | null;
}

export async function createCalendarPeriod(token: string, draft: PeriodDraft): Promise<string> {
  return apiJson(token, "/calendar-periods", (body) => str((body as Record<string, unknown>).id), {
    method: "POST",
    body: {
      kind: draft.kind,
      name: draft.name,
      starts_on: draft.startsOn,
      ends_on: draft.endsOn,
      project_id: draft.projectId,
      note: draft.note,
    },
  });
}

/* ---------------------------------------------------------------------- */
/* The stage-linked taxonomies. The stages carry their row ids and the     */
/* verification types their stage links (M4 P3 step 0), so the program     */
/* lenses and the calendar filters can group without inventing a mapping.  */
/* ---------------------------------------------------------------------- */

export interface StageToken {
  readonly id: string;
  readonly token: string;
  readonly label: string;
}

export interface TaxonomyStages {
  readonly stages: readonly StageToken[];
  /** verification type code -> the stage token it is linked to, null when cross-stage. */
  readonly typeStage: Readonly<Record<string, string>>;
}

export async function getStageTaxonomies(
  token: string,
  signal?: AbortSignal,
): Promise<TaxonomyStages> {
  return apiJson(
    token,
    "/taxonomies",
    (body) => {
      if (typeof body !== "object" || body === null) {
        throw new Error("Expected the taxonomies body");
      }
      const record = body as Record<string, unknown>;
      const rawStages = record.stages;
      if (!Array.isArray(rawStages)) {
        throw new Error("Expected the stage register");
      }
      const stages = rawStages.map((entry) => {
        if (typeof entry !== "object" || entry === null) {
          throw new Error("Expected a stage token");
        }
        const one = entry as Record<string, unknown>;
        return {
          id: str(one.id),
          token: str(one.token),
          label: str(one.label),
        } satisfies StageToken;
      });
      const rawTypes = record.verification_types;
      if (!Array.isArray(rawTypes)) {
        throw new Error("Expected the verification type register");
      }
      const stageById = new Map(stages.map((stage) => [stage.id, stage.token]));
      const typeStage: Record<string, string> = {};
      for (const entry of rawTypes) {
        if (typeof entry !== "object" || entry === null) {
          throw new Error("Expected a verification type token");
        }
        const one = entry as Record<string, unknown>;
        const code = str(one.token);
        if (one.stage_id === null || one.stage_id === undefined) {
          continue;
        }
        const stageToken = stageById.get(str(one.stage_id));
        if (stageToken !== undefined) {
          typeStage[code] = stageToken;
        }
      }
      return { stages, typeStage } satisfies TaxonomyStages;
    },
    { signal },
  );
}

/** The count reader the page needs for its own arithmetic. */
export { num };
